import React, { useEffect } from "react";
import CommonView, { CommonViewProps } from "../../../components/CommonView/CommonView";
import { MasterConfigs } from "../../../interfaces/entity";
import MasterComponent from "../../../components/Forms";
import { Tag, message, Button, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../../actions/apiCall";
import axios from "axios";
import CustomModal from "../../../components/custom/Modal";
import ParseCsv from "../../../components/CSV/ParseCsv";
import moment from "moment";


const AIInterviewQuestions: React.FC = () => {
    const dispatch = useDispatch();
    const [refresh, setRefresh] = React.useState(false);
    const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(null);
    const [importModel, setImportModel] = React.useState(false);
    const [uploadData, setUploadData] = React.useState<any[]>([]);

    // Sample data for CSV template
    const [sampleData, setSampleData] = React.useState<any>([
        ['Question', 'Level', 'Skills (comma-separated skill names)'],
        ['What is the difference between let and var in JavaScript?', 'medium', 'JavaScript, ES6'],
        ['Can API Gateway become a single point of failure?', 'hard', 'Microservices, API Gateway, System Design'],
        ['Client-side vs server-side service discovery.', 'medium', 'Microservices, Service Discovery'],
    ]);

    // Download Excel template
    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(
                `${process.env.API_URL}/accel-test/ai-interview/template/download`,
                {
                    responseType: 'blob',
                }
            );

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'ai_interview_questions_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Template downloaded successfully');
        } catch (error: any) {
            console.error('Error downloading template:', error);
            message.error(error?.response?.data?.message || 'Failed to download template');
        }
    };

    // Config for left sidebar (skills list)
    const config: MasterConfigs = {
        api: () => '/accel-test/ai-interview/skills',
        enableListSearch: true,
        listSearchKeys: ['name'],
        queryParams: { status: 'active', pageSize: 15 },
        modalTitle: () => 'Skill',
        onRefresh: () => {
            setRefresh(true);
        },
        getFormColumns: () => [
            {
                label: 'Skill Name',
                name: 'name',
                type: 'text',
                rules: [{ required: true, message: 'Please enter skill name' }]
            },
            {
                label: 'Short Code',
                name: 'shortCode',
                type: 'text',
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 16,
                inputTextAreaProps: {
                    rows: 3
                }
            },
        ],
    };


    const questionConfig: MasterConfigs = React.useMemo(() => ({
        api: () => {
            if (!selectedSkillId) {
                return null;
            }
            // Use the question bank endpoint - no test setup dependency
            return `/accel-test/ai-interview/questions/bank`;
        },
        queryParams: {
            skillId: selectedSkillId,
            pageSize: 10
        },
        apiParams: {},
        onRefresh: () => {
            setRefresh(true);
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            });
        },
        modalTitle: () => 'AI Interview Question',
        isActionTab: true,
        loadPagination: true,
        setInitialForm: () => {
            // Set initial form values to include selected skill
            if (selectedSkillId) {
                return {
                    skills: [selectedSkillId]
                };
            }
            return {};
        },
        afterSubmit: () => {
            // Refresh table after successful submission
            setRefresh(true);
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            });
        },
        getColumns: () => [
            {
                title: 'S.No',
                key: 'index',
                width: 80,
                render: (_: any, __: any, index: number) => index + 1,
            },
            {
                title: 'Question',
                dataIndex: 'question',
                key: 'question',
                ellipsis: true,
                render: (text: string) => (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                ),
            },
            {
                title: 'Level',
                dataIndex: 'level',
                key: 'level',
                width: 120,
                render: (level: string) => {
                    const colorMap: any = {
                        easy: 'green',
                        medium: 'orange',
                        hard: 'red',
                    };
                    return (
                        <Tag color={colorMap[level] || 'default'}>
                            {level?.toUpperCase() || 'MEDIUM'}
                        </Tag>
                    );
                },
            },
            {
                title: 'createdAt',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 200,
                render: (createdAt: string) => {
                    return moment(createdAt).format('LLL');
                },
            }
        ],
        getFormColumns: () => {
            if (!selectedSkillId) {
                return [];
            }
            return [
                {
                    label: 'Question',
                    name: 'question',
                    type: 'textarea',
                    span: 24,
                    rules: [{ required: true, message: 'Please enter question' }],
                    placeholder: 'Enter your interview question here...',
                    inputTextAreaProps: {
                        rows: 3
                    }
                },
                {
                    label: 'Skills',
                    name: 'skills',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                    selectProps: {
                        mode: 'multiple',
                        placeholder: 'Select skills for this question'
                    },
                    rules: [{ required: true, message: 'Please select at least one skill' }]
                },
                {
                    label: 'Difficulty Level',
                    name: 'level',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        placeholder: 'Select level'
                    },
                    children: [
                        { name: 'Easy', value: 'easy' },
                        { name: 'Medium', value: 'medium' },
                        { name: 'Hard', value: 'hard' }
                    ],
                    rules: [{ required: true, message: 'Please select difficulty level' }],
                    initialValue: 'medium'
                }
            ];
        },
        combo: [
            {
                api: '/skills',
                name: 'skills',
                idKey: '_id',
                render: (e) => e.name
            }
        ],
        actions: [
            { actionType: 'Add', name: 'Add' }
        ],
        extraControles: [
            <Space key="actions" style={{ marginLeft: 16 }}>
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                >
                    Download Template
                </Button>
                <Button
                    type="primary"
                    onClick={() => {
                        if (!selectedSkillId) {
                            message.warning('Please select a skill first');
                            return;
                        }
                        setImportModel(true);
                    }}
                >
                    Import
                </Button>
            </Space>
        ],
    }), [selectedSkillId, dispatch]);

    React.useEffect(() => {
        if (selectedSkillId) {
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            });
        }
    }, [selectedSkillId, dispatch]);

    const componentConfig: CommonViewProps = {
        config: config,
        listProps: {
            reload: refresh,
            itemsPerPage: 10,
            onLoaded: () => {
                setRefresh(false);
            }
        },
        componentConfig: {
            title: "AI Interview Questions",
            subTitle: "AI Interview Questions",
            cardTitle: "AI Interview Questions",
            buttonName: "Add Question",
            emptyDescription: selectedSkillId ? "No questions found for this skill" : "Please select a skill to view questions",
            renderComponent: (record: any) => {
                return <MasterComponent config={questionConfig} />;
            },
            renderListItem: (record: any) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>{record.name}</span>
                    {record.questionCount !== undefined && (
                        <span style={{
                            marginLeft: 8,
                            color: '#666',
                            fontSize: '12px',
                            backgroundColor: '#f0f0f0',
                            padding: '2px 8px',
                            borderRadius: '12px'
                        }}>
                            {record.questionCount}
                        </span>
                    )}
                </div>
            ),
            selectedItemId: (id) => setSelectedSkillId(id),
        }
    };

    const reloadTable = () => {
        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true,
        });
    };

    const validateData = (data: any[]) => {
        if (!data || data.length === 0) {
            console.log('validateData: No data provided');
            return [];
        }

        const validData = data.filter((item, index) => {
            const findValue = (keys: string[], searchTerm: string) => {
                for (const key of keys) {
                    if (key.toLowerCase() === searchTerm.toLowerCase()) {
                        return item[key];
                    }
                }
                // Then try substring matches
                for (const key of keys) {
                    if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
                        return item[key];
                    }
                }
                return null;
            };

            const keys = Object.keys(item);
            const question = findValue(keys, 'question');
            const level = findValue(keys, 'level');
            const skills = findValue(keys, 'skill');

            // Check if fields exist and are not empty
            const hasQuestion = question && String(question).trim() !== '';
            const hasLevel = level && String(level).trim() !== '';
            const hasSkills = skills && String(skills).trim() !== '';

            if (!hasQuestion || !hasLevel || !hasSkills) {
                console.log(`Row ${index + 1} invalid:`, {
                    hasQuestion,
                    hasLevel,
                    hasSkills,
                    itemKeys: Object.keys(item),
                    sample: item
                });
                return false;
            }

            // Validate level (case-insensitive)
            const validLevels = ['easy', 'medium', 'hard'];
            const normalizedLevel = String(level).toLowerCase().trim();
            if (!validLevels.includes(normalizedLevel)) {
                console.log(`Row ${index + 1} has invalid level:`, normalizedLevel);
                return false;
            }

            return true;
        });

        console.log('validateData: Valid rows:', validData.length, 'out of', data.length);
        return validData;
    };

    const onSubmit = async () => {
        if (!selectedSkillId) {
            message.error('Please select a skill first');
            return;
        }

        if (uploadData.length === 0) {
            message.warning('No data to upload');
            return;
        }

        console.log('onSubmit: uploadData sample:', uploadData[0]);
        console.log('onSubmit: uploadData keys:', uploadData.length > 0 ? Object.keys(uploadData[0]) : 'empty');

        const validated = validateData(uploadData);
        if (validated.length === 0) {
            const sampleRow = uploadData[0] || {};
            const availableKeys = Object.keys(sampleRow);
            message.error(`No valid data found. Please ensure your file has columns: Question, Level, Skills. Found columns: ${availableKeys.join(', ') || 'none'}`);
            return;
        }

        if (validated.length < uploadData.length) {
            message.warning(`${uploadData.length - validated.length} row(s) were skipped due to invalid data.`);
        }

        try {
            // Helper function to find value by key (case-insensitive and substring match)
            const findValue = (item: any, searchTerm: string) => {
                const keys = Object.keys(item);
                // First try exact matches (case-insensitive)
                for (const key of keys) {
                    if (key.toLowerCase() === searchTerm.toLowerCase()) {
                        return item[key];
                    }
                }
                // Then try substring matches
                for (const key of keys) {
                    if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
                        return item[key];
                    }
                }
                return null;
            };

            // Convert validated data to format expected by bulk endpoint
            const csvRows = validated.map((item, index) => {
                const question = findValue(item, 'question') || '';
                const level = (findValue(item, 'level') || '').toString().toLowerCase().trim();

                // Try multiple ways to find skills
                let skills = findValue(item, 'skill') || '';

                // If not found, try direct key access with different cases
                if (!skills) {
                    const allKeys = Object.keys(item);
                    const skillKey = allKeys.find(key => {
                        const lower = key.toLowerCase();
                        return lower.includes('skill') && !lower.includes('display');
                    });
                    if (skillKey) {
                        skills = item[skillKey] || '';
                        console.log(`Found skills using direct key access: "${skillKey}" = "${skills}"`);
                    }
                }

                // Debug logging for first row
                if (index === 0) {
                    console.log('First row extraction:', {
                        originalItem: item,
                        extractedQuestion: question,
                        extractedLevel: level,
                        extractedSkills: skills,
                        allKeys: Object.keys(item),
                        allValues: Object.entries(item).map(([k, v]) => ({ key: k, value: v, type: typeof v }))
                    });
                }

                // Ensure skills is a string (not null/undefined)
                const skillsString = skills ? String(skills).trim() : '';

                if (!skillsString && index === 0) {
                    console.warn('Skills not found in first row. Available keys:', Object.keys(item));
                    console.warn('Item values:', item);
                    console.warn('Trying to find skills manually...');
                    // Try to find skills with different key variations
                    const allKeys = Object.keys(item);
                    allKeys.forEach(key => {
                        if (key.toLowerCase().includes('skill')) {
                            console.warn(`Found potential skills key: "${key}" with value: "${item[key]}" (type: ${typeof item[key]})`);
                        }
                    });
                }

                return {
                    Question: question.trim(),
                    Level: level,
                    Skills: skillsString
                };
            });

            // Check for duplicate questions (case-insensitive)
            const questionMap = new Map<string, number[]>();
            const duplicates: number[] = [];

            csvRows.forEach((row, index) => {
                const questionKey = row.Question.toLowerCase().trim();
                if (questionMap.has(questionKey)) {
                    const existingIndices = questionMap.get(questionKey)!;
                    existingIndices.push(index + 1); // +1 for 1-based row numbering
                    duplicates.push(...existingIndices);
                } else {
                    questionMap.set(questionKey, [index + 1]);
                }
            });

            if (duplicates.length > 0) {
                const uniqueDuplicates = Array.from(new Set(duplicates));
                const duplicateQuestions = uniqueDuplicates.map(rowNum => {
                    const row = csvRows[rowNum - 1];
                    return `Row ${rowNum}: "${row.Question}"`;
                }).join('\n');

                message.error(`Duplicate questions found. Please remove duplicates before submitting:\n${duplicateQuestions}`);
                return;
            }

            // Log final data being sent
            console.log('Final data being sent to API:', {
                totalRows: csvRows.length,
                sampleRow: csvRows[0],
                sampleRowSkills: csvRows[0]?.Skills,
                allRows: csvRows.map((row, idx) => ({
                    row: idx + 1,
                    question: row.Question,
                    level: row.Level,
                    skills: row.Skills,
                    skillsType: typeof row.Skills,
                    skillsLength: row.Skills ? row.Skills.length : 0
                }))
            });

            // Use bulk import endpoint for question bank (no test setup needed)
            const response = await axios.post(
                `${process.env.API_URL}/accel-test/ai-interview/questions/bank/bulk`,
                { questions: csvRows },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data?.code === 200) {
                const result = response.data.data || response.data;
                message.success(result.message || `Successfully imported ${validated.length} question(s)`);
                setImportModel(false);
                reloadTable();
                setUploadData([]);
            } else {
                message.error(response.data?.message || 'Failed to import questions');
            }
        } catch (error: any) {
            console.error('Error importing questions:', error);
            message.error(error?.response?.data?.message || 'Failed to import questions');
        }
    };

    const onDataSet = (data: any[]) => {
        setUploadData(data);
    };

    return (
        <>
            <CustomModal
                visible={importModel}
                title="Import AI Interview Questions"
                onCancel={() => {
                    setImportModel(false);
                    setUploadData([]);
                }}
                footer={
                    <Button type="primary" onClick={onSubmit}>
                        Submit
                    </Button>
                }
            >
                <ParseCsv onDataload={onDataSet} sampleData={sampleData} />
            </CustomModal>
            <CommonView componentConfig={componentConfig.componentConfig} config={componentConfig.config} />
        </>
    );
};

export default AIInterviewQuestions;
